import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { RefreshCw, CheckCircle, XCircle, DollarSign, Search, Eye, X, Mail } from 'lucide-react'
import { returnsService } from '@/services/returnsService'
import { usePagination } from '@/hooks/usePagination'
import { useDebounce } from '@/hooks/useDebounce'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import Pagination from '@/components/shared/Pagination'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { ROUTES } from '@/constants/routes'
import { formatCurrency, formatRelativeTime, getLocalizedField } from '@/utils/formatters'

const STATUS_TABS = [
  { key: '',          label: 'الكل',          color: '#767683' },
  { key: 'pending',   label: 'قيد المراجعة',  color: '#FF6D00' },
  { key: 'completed', label: 'مُسترد',        color: '#1B6D24' },
  { key: 'failed',    label: 'مرفوض',         color: '#BA1A1A' },
]

const STATUS_META = {
  pending:   { label: 'قيد المراجعة', color: '#FF6D00', bg: 'rgba(255,109,0,0.08)' },
  completed: { label: 'مُسترد',       color: '#1B6D24', bg: 'rgba(27,109,36,0.08)' },
  failed:    { label: 'مرفوض',        color: '#BA1A1A', bg: 'rgba(186,26,26,0.08)' },
}

export default function ReturnsPage() {
  const { i18n } = useTranslation()
  const lang = i18n.language?.startsWith('ar') ? 'ar' : 'en'
  const qc = useQueryClient()
  const { page, limit, setPage } = usePagination(1, 15)
  const [activeTab, setActiveTab] = useState('')
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 400)

  const [approveDialog, setApproveDialog] = useState({ open: false, id: null })
  const [rejectDialog, setRejectDialog]   = useState({ open: false, id: null, note: '' })
  const [detailDialog, setDetailDialog]   = useState({ open: false, item: null })

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['returns-admin', { status: activeTab, search: debouncedSearch, page, limit }],
    queryFn: () => returnsService.getAll({
      status: activeTab || undefined,
      search: debouncedSearch || undefined,
      page,
      limit,
    }),
    retry: 1,
  })

  const returns = data?.data || []
  const meta    = data?.meta || {}

  const approveMut = useMutation({
    mutationFn: (id) => returnsService.approve(id),
    onSuccess: () => {
      toast.success('تم الموافقة على المرتجع')
      qc.invalidateQueries({ queryKey: ['returns-admin'] })
      setApproveDialog({ open: false, id: null })
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'فشل الموافقة'),
  })

  const contactCustomer = (ret) => {
    const email = ret.order?.user?.email
    if (!email) { toast.error('لا يوجد بريد إلكتروني للعميل'); return }
    const subject = encodeURIComponent(`بخصوص طلب الإرجاع — طلب #${ret.order?.orderNumber || ''}`)
    window.open(`mailto:${email}?subject=${subject}`, '_blank')
  }

  const rejectMut = useMutation({
    mutationFn: ({ id, note }) => returnsService.reject(id, note),
    onSuccess: () => {
      toast.success('تم رفض طلب المرتجع')
      qc.invalidateQueries({ queryKey: ['returns-admin'] })
      setRejectDialog({ open: false, id: null, note: '' })
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'فشل الرفض'),
  })

  return (
    <div className="space-y-5">

      {/* Header */}
      <div>
        <h1 className="font-black text-navy text-xl" style={{ fontFamily: 'Cairo, sans-serif' }}>المرتجعات والمبالغ المستردة</h1>
        <p className="text-sm text-ghost mt-1" style={{ fontFamily: 'Tajawal, sans-serif' }}>مراجعة طلبات الإرجاع وإدارة المبالغ المستردة</p>
      </div>

      {/* Filters */}
      <div className="hg-card p-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-2 flex-wrap">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setPage(1) }}
              className="px-3 py-1.5 rounded-xl text-xs font-bold transition-all"
              style={{
                background: activeTab === tab.key ? tab.color : `${tab.color}12`,
                color: activeTab === tab.key ? '#fff' : tab.color,
                fontFamily: 'Tajawal, sans-serif',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-48">
          <Search size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#767683', pointerEvents: 'none' }} />
          <input className="hg-input" style={{ paddingRight: 34 }}
            placeholder="ابحث برقم الطلب أو العميل..."
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} />
        </div>
      </div>

      {/* Table */}
      <div className="hg-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20"><LoadingSpinner /></div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <p style={{ color: '#BA1A1A', fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>فشل تحميل المرتجعات</p>
            <button onClick={() => refetch()} className="hg-btn-navy px-5 py-2 text-sm">إعادة المحاولة</button>
          </div>
        ) : returns.length === 0 ? (
          <EmptyState icon={RefreshCw} message="لا توجد طلبات مرتجعات بهذه المعايير" />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F9F9F9', borderBottom: '2px solid #F3F3F3' }}>
                    {['رقم الطلب', 'العميل', 'المبلغ', 'السبب', 'الحالة', 'التاريخ', 'الإجراءات'].map((h) => (
                      <th key={h} style={{ textAlign: 'right', padding: '12px 14px', fontSize: 11, fontWeight: 700, color: '#767683', whiteSpace: 'nowrap', fontFamily: 'Tajawal, sans-serif' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {returns.map((ret, idx) => {
                    const sm = STATUS_META[ret.status] || STATUS_META.pending
                    return (
                      <tr key={ret.id} style={{ borderBottom: idx === returns.length - 1 ? 'none' : '1px solid #F3F3F3' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#F9F9F9'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 14px' }}>
                          {ret.order?.orderNumber ? (
                            <Link
                              to={ROUTES.ORDER_DETAIL.replace(':orderNumber', ret.order.orderNumber)}
                              style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, color: '#FF6D00', textDecoration: 'underline' }}
                              title="عرض الطلب"
                            >
                              {ret.order.orderNumber}
                            </Link>
                          ) : (
                            <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, color: '#000666' }}>
                              —
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 12, color: '#1A1C1C' }}>{ret.order?.user?.name || '—'}</div>
                          <div style={{ fontSize: 11, color: '#767683', fontFamily: 'Tajawal, sans-serif' }}>{ret.order?.user?.email || ''}</div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 800, color: '#FF6D00', fontSize: 14 }}>
                            {formatCurrency(ret.amount || 0)}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', maxWidth: 180 }}>
                          <span style={{ fontSize: 12, color: '#454652', fontFamily: 'Tajawal, sans-serif', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {ret.reason || '—'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sm.bg, color: sm.color, fontFamily: 'Tajawal, sans-serif', whiteSpace: 'nowrap' }}>
                            {sm.label}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ fontSize: 11, color: '#767683', fontFamily: 'Tajawal, sans-serif' }}>
                            {formatRelativeTime(ret.createdAt)}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => setDetailDialog({ open: true, item: ret })}
                              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 8, background: 'rgba(0,6,102,0.08)', color: '#000666', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                              <Eye size={12} />
                              التفاصيل
                            </button>
                            {ret.status === 'pending' && (
                              <>
                                <button onClick={() => setApproveDialog({ open: true, id: ret.id })}
                                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 8, background: 'rgba(27,109,36,0.08)', color: '#1B6D24', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                                  <CheckCircle size={12} />
                                  موافقة
                                </button>
                                <button onClick={() => setRejectDialog({ open: true, id: ret.id, note: '' })}
                                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 8, background: 'rgba(186,26,26,0.08)', color: '#BA1A1A', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                                  <XCircle size={12} />
                                  رفض
                                </button>
                              </>
                            )}
                            <button onClick={() => contactCustomer(ret)}
                              title="التواصل مع العميل"
                              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 8, background: 'rgba(0,119,182,0.08)', color: '#0077B6', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                              <Mail size={12} />
                              تواصل
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '0 16px 16px' }}>
              <Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={limit} onPageChange={setPage} />
            </div>
          </>
        )}
      </div>

      {/* Approve Dialog */}
      <ConfirmDialog
        open={approveDialog.open}
        title="الموافقة على المرتجع"
        message="هل تريد الموافقة على طلب الإرجاع هذا؟ سيتم تغيير حالة الطلب إلى مُسترد."
        variant="success"
        loading={approveMut.isPending}
        onConfirm={() => approveMut.mutate(approveDialog.id)}
        onCancel={() => setApproveDialog({ open: false, id: null })}
      />

      {/* Reject Dialog */}
      {rejectDialog.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
          onClick={() => setRejectDialog({ open: false, id: null, note: '' })}
        >
          <div
            className="hg-card p-6 w-full max-w-sm animate-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4 mb-5">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(186,26,26,0.1)', color: '#BA1A1A' }}
              >
                <XCircle size={20} />
              </div>
              <div className="flex-1">
                <h3 className="font-black text-ink text-base mb-1" style={{ fontFamily: 'Cairo, sans-serif' }}>
                  رفض طلب المرتجع
                </h3>
                <p className="text-sm text-dim mb-3" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                  أدخل سبب الرفض (اختياري)
                </p>
                <textarea
                  className="hg-input w-full"
                  rows={3}
                  placeholder="سبب الرفض..."
                  value={rejectDialog.note}
                  onChange={(e) => setRejectDialog((prev) => ({ ...prev, note: e.target.value }))}
                  style={{ fontFamily: 'Tajawal, sans-serif', resize: 'vertical' }}
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => rejectMut.mutate({ id: rejectDialog.id, note: rejectDialog.note })}
                disabled={rejectMut.isPending}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm text-white transition-all"
                style={{ background: '#BA1A1A', fontFamily: 'Cairo, sans-serif', opacity: rejectMut.isPending ? 0.6 : 1 }}
              >
                {rejectMut.isPending ? '...' : 'تأكيد الرفض'}
              </button>
              <button
                onClick={() => setRejectDialog({ open: false, id: null, note: '' })}
                disabled={rejectMut.isPending}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm transition-all"
                style={{ background: '#F3F3F3', color: '#454652', fontFamily: 'Cairo, sans-serif' }}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Dialog */}
      {detailDialog.open && detailDialog.item && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
          onClick={() => setDetailDialog({ open: false, item: null })}
        >
          <div
            className="hg-card p-6 w-full max-w-lg animate-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-black text-navy text-lg" style={{ fontFamily: 'Cairo, sans-serif' }}>تفاصيل طلب المرتجع</h3>
              <button
                onClick={() => setDetailDialog({ open: false, item: null })}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
              >
                <X size={16} color="#767683" />
              </button>
            </div>

            {(() => {
              const ret = detailDialog.item
              const sm = STATUS_META[ret.status] || STATUS_META.pending
              return (
                <div className="space-y-4">
                  {/* Status */}
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-ghost" style={{ fontFamily: 'Tajawal, sans-serif', minWidth: 70 }}>الحالة</span>
                    <span style={{ padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: sm.bg, color: sm.color, fontFamily: 'Tajawal, sans-serif' }}>
                      {sm.label}
                    </span>
                  </div>

                  {/* Order Number */}
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-ghost" style={{ fontFamily: 'Tajawal, sans-serif', minWidth: 70 }}>رقم الطلب</span>
                    {ret.order?.orderNumber ? (
                      <Link
                        to={ROUTES.ORDER_DETAIL.replace(':orderNumber', ret.order.orderNumber)}
                        style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 14, color: '#FF6D00', textDecoration: 'underline' }}
                        title="عرض الطلب"
                        onClick={() => setDetailDialog({ open: false, item: null })}
                      >
                        {ret.order.orderNumber}
                      </Link>
                    ) : (
                      <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 14, color: '#000666' }}>
                        —
                      </span>
                    )}
                  </div>

                  {/* Customer */}
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-ghost" style={{ fontFamily: 'Tajawal, sans-serif', minWidth: 70 }}>العميل</span>
                    <div>
                      <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, color: '#1A1C1C' }}>{ret.order?.user?.name || '—'}</div>
                      <div style={{ fontSize: 12, color: '#767683', fontFamily: 'Tajawal, sans-serif' }}>{ret.order?.user?.email || ''}</div>
                    </div>
                  </div>

                  {/* Product */}
                  {ret.orderItem && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-ghost" style={{ fontFamily: 'Tajawal, sans-serif', minWidth: 70 }}>المنتج</span>
                      <div>
                        <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13, color: '#1A1C1C' }}>
                          {getLocalizedField(ret.orderItem.product?.name, lang) || getLocalizedField(ret.orderItem.productSnapshot?.name, lang) || '—'}
                        </div>
                        <div style={{ fontSize: 12, color: '#767683', fontFamily: 'Tajawal, sans-serif' }}>
                          الكمية: {ret.orderItem.quantity}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Amount */}
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-ghost" style={{ fontFamily: 'Tajawal, sans-serif', minWidth: 70 }}>المبلغ</span>
                    <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 800, color: '#FF6D00', fontSize: 16 }}>
                      {formatCurrency(ret.amount || 0)}
                    </span>
                  </div>

                  {/* Payment Method */}
                  {ret.payment && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-ghost" style={{ fontFamily: 'Tajawal, sans-serif', minWidth: 70 }}>طريقة الدفع</span>
                      <span style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 13, color: '#454652' }}>
                        {ret.payment.method || '—'}
                      </span>
                    </div>
                  )}

                  {/* Reason */}
                  <div className="flex items-start gap-3">
                    <span className="text-xs text-ghost" style={{ fontFamily: 'Tajawal, sans-serif', minWidth: 70 }}>السبب</span>
                    <p style={{ fontSize: 13, color: '#454652', fontFamily: 'Tajawal, sans-serif', lineHeight: 1.6, margin: 0 }}>
                      {ret.reason || '—'}
                    </p>
                  </div>

                  {/* Date */}
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-ghost" style={{ fontFamily: 'Tajawal, sans-serif', minWidth: 70 }}>التاريخ</span>
                    <span style={{ fontSize: 12, color: '#767683', fontFamily: 'Tajawal, sans-serif' }}>
                      {formatRelativeTime(ret.createdAt)}
                    </span>
                  </div>

                  {/* Processed At */}
                  {ret.processedAt && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-ghost" style={{ fontFamily: 'Tajawal, sans-serif', minWidth: 70 }}>تاريخ المعالجة</span>
                      <span style={{ fontSize: 12, color: '#767683', fontFamily: 'Tajawal, sans-serif' }}>
                        {formatRelativeTime(ret.processedAt)}
                      </span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-3 border-t" style={{ borderColor: '#F3F3F3' }}>
                    {ret.status === 'pending' && (
                      <>
                        <button
                          onClick={() => { setDetailDialog({ open: false, item: null }); setApproveDialog({ open: true, id: ret.id }) }}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-sm text-white transition-all"
                          style={{ background: '#1B6D24', fontFamily: 'Cairo, sans-serif' }}
                        >
                          <CheckCircle size={14} />
                          موافقة
                        </button>
                        <button
                          onClick={() => { setDetailDialog({ open: false, item: null }); setRejectDialog({ open: true, id: ret.id, note: '' }) }}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-sm text-white transition-all"
                          style={{ background: '#BA1A1A', fontFamily: 'Cairo, sans-serif' }}
                        >
                          <XCircle size={14} />
                          رفض
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => contactCustomer(ret)}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-sm transition-all"
                      style={{ background: 'rgba(0,119,182,0.1)', color: '#0077B6', fontFamily: 'Cairo, sans-serif', border: 'none', cursor: 'pointer' }}
                    >
                      <Mail size={14} />
                      التواصل مع العميل
                    </button>
                  </div>
                </div>
              )
            })()}
          </div>
        </div>
      )}
    </div>
  )
}
